// Environment configuration for the application

export interface EnvironmentConfig {
  apiUrl: string;
  userPoolId: string;
  userPoolClientId: string;
  environment: string;
  isDevelopment: boolean;
  isProduction: boolean;
  mockAuth: boolean;
  mockAuthEmail: string;
  mockAuthPassword: string;
}

// Get environment variables with fallbacks
const getEnvVar = (key: string, fallback?: string): string => {
  const value = import.meta.env[key] || fallback;
  if (!value) {
    console.warn(`Environment variable ${key} is not set`);
  }
  return value || '';
};

const environmentName = getEnvVar('VITE_ENVIRONMENT', 'development');
const isDevelopment = environmentName === 'development';
const isProduction = environmentName === 'production';
const mockAuthEnabled = isDevelopment && getEnvVar('VITE_MOCK_AUTH', 'false') === 'true';

// Create environment configuration
export const environment: EnvironmentConfig = {
  apiUrl: getEnvVar('VITE_API_URL', '/api'),
  userPoolId: getEnvVar('VITE_USER_POOL_ID', ''),
  userPoolClientId: getEnvVar('VITE_USER_POOL_CLIENT_ID', ''),
  environment: environmentName,
  isDevelopment,
  isProduction,
  mockAuth: mockAuthEnabled,
  mockAuthEmail: getEnvVar('VITE_MOCK_AUTH_EMAIL', 'admin@congplanner.local'),
  mockAuthPassword: getEnvVar('VITE_MOCK_AUTH_PASSWORD', 'dev-admin-password'),
};

// Validate required environment variables in production
if (environment.isProduction) {
  const requiredVars = [
    { key: 'VITE_API_URL', value: environment.apiUrl },
    { key: 'VITE_USER_POOL_ID', value: environment.userPoolId },
    { key: 'VITE_USER_POOL_CLIENT_ID', value: environment.userPoolClientId },
  ];

  const missingVars = requiredVars.filter(({ value }) => !value);
  
  if (missingVars.length > 0) {
    const missing = missingVars.map(({ key }) => key).join(', ');
    throw new Error(`Missing required environment variables: ${missing}`);
  }
}

// Log configuration in development
if (environment.isDevelopment) {
  console.log('Environment Configuration:', {
    ...environment,
    // Don't log sensitive values in production
    userPoolId: environment.userPoolId ? '[SET]' : '[NOT SET]',
    userPoolClientId: environment.userPoolClientId ? '[SET]' : '[NOT SET]',
  });
}

export default environment;
